/*
    Admin credit-consumption page (PIN-358, PIN-363).

    Totals how many credits were spent over an optional date range: per test (battery), per
    customer (credit pool) and test alongside each pool's remaining credits, so idle customers
    stand out, and per report for sales and author-royalty reporting. The summaries come from
    /transaction/consumption; the CSV downloads from /transaction/consumption/csv with a `view`.
    "From"/"To" are inclusive UTC days; leave both empty for all time.
*/

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dashboard from '../dashboard';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { useSelector } from 'react-redux';
import cardStyles from '../../style/info-card.module.css';
import { Button, notification } from 'antd';
import history from '../../history';

const apiUrl = process.env.REACT_APP_API_URL;

// request body: only the dates the admin actually entered
const rangeBody = ({ from, to }) => {
  const body = {};
  if (from) body.from = from;
  if (to) body.to = to;
  return body;
};

// a battery-less row (deleted report, or a customer's zero-use row) has no batteryID
const batteryKey = (row) => row.batteryID || 'none';

const authConfig = (token) => ({
  headers: { 'Content-Type': 'application/json', Authorization: 'Token ' + token }
});

const CreditConsumptionPage = () => {
  const token = useSelector((state) => state.token);
  const userInfo = useSelector((state) => state.userInfo);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // the range the table (and CSV) currently reflect; changes only on Apply
  const [applied, setApplied] = useState({ from: '', to: '' });
  const [rows, setRows] = useState([]);
  const [byTest, setByTest] = useState([]);
  const [byCustomer, setByCustomer] = useState([]);
  // the CSV view being downloaded, if any
  const [downloading, setDownloading] = useState(null);

  // Responses can arrive out of order (the mount load vs. an early Apply); only the most
  // recently started request may update the table, or a slow all-time load would clobber it.
  const latestRequest = useRef(0);

  const load = useCallback(
    async (range) => {
      const request = ++latestRequest.current;
      try {
        const response = await axios.post(
          `${apiUrl}/transaction/consumption`,
          rangeBody(range),
          authConfig(token)
        );
        if (request !== latestRequest.current) return;
        setRows(response.data.rows);
        setByTest(response.data.byTest);
        setByCustomer(response.data.byCustomer);
        setApplied(range);
      } catch (error) {
        if (request !== latestRequest.current) return;
        notification.error({
          message: 'Failed to load credit consumption',
          description: error.message
        });
      }
    },
    [token]
  );

  // Redirect non-admin users
  useEffect(() => {
    if (userInfo.kind && userInfo.kind !== 'Administrator') {
      history.push('/');
    }
  }, [userInfo.kind]);

  // The API is admin-only, so a non-admin's load can only 403 and flash error toasts before the
  // redirect above takes effect.
  const isAdmin = userInfo.kind === 'Administrator';
  useEffect(() => {
    if (isAdmin) load({ from: '', to: '' });
  }, [load, isAdmin]);

  // view is undefined for the per-report CSV, the API's default
  const downloadCsv = async (view, filename) => {
    setDownloading(view || 'report');
    try {
      const response = await axios.post(
        `${apiUrl}/transaction/consumption/csv`,
        view ? { ...rangeBody(applied), view } : rangeBody(applied),
        authConfig(token)
      );
      fileDownload(response.data, filename);
    } catch (error) {
      notification.error({ message: 'Failed to download CSV', description: error.message });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Dashboard>
      <div className={cardStyles['card']}>
        <div className={cardStyles['header']}>
          <label className={cardStyles['name']}>Credit Consumption</label>
        </div>
        <hr className={cardStyles['horizontal-line']}></hr>

        <div>
          <label>
            From{' '}
            <input
              type="date"
              aria-label="From"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>{' '}
          <label>
            To{' '}
            <input type="date" aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>{' '}
          <Button data-testid="consumption-apply" onClick={() => load({ from, to })}>
            Apply
          </Button>
        </div>

        <h3>
          By test{' '}
          <Button
            size="small"
            data-testid="consumption-download-test-csv"
            loading={downloading === 'test'}
            onClick={() => downloadCsv('test', 'credit-consumption-by-test.csv')}
          >
            Download CSV
          </Button>
        </h3>
        {byTest.length === 0 ? (
          <p>No tests used in this period.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Test</th>
                <th>Credits Used</th>
              </tr>
            </thead>
            <tbody>
              {byTest.map((row) => (
                <tr key={batteryKey(row)}>
                  <td>{row.batteryName}</td>
                  <td data-testid={`consumption-test-credits-${batteryKey(row)}`}>
                    {row.creditsUsed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3>
          By report{' '}
          <Button
            size="small"
            data-testid="consumption-download-csv"
            loading={downloading === 'report'}
            onClick={() => downloadCsv(undefined, 'credit-consumption.csv')}
          >
            Download CSV
          </Button>
        </h3>
        {rows.length === 0 ? (
          <p>No credits used in this period.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Report</th>
                <th>Battery</th>
                <th>Credits Used</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.reportID}>
                  <td>{row.reportName}</td>
                  <td>{row.batteryName}</td>
                  <td data-testid={`consumption-credits-${row.reportID}`}>{row.creditsUsed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3>
          By customer{' '}
          <Button
            size="small"
            data-testid="consumption-download-customer-csv"
            loading={downloading === 'customer'}
            onClick={() => downloadCsv('customer', 'credit-consumption-by-customer.csv')}
          >
            Download CSV
          </Button>
        </h3>
        {byCustomer.length === 0 ? (
          <p>No customers.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Owner</th>
                <th>Organization</th>
                <th>Test</th>
                <th>Credits Used</th>
                <th>Credits Remaining</th>
              </tr>
            </thead>
            <tbody>
              {byCustomer.map((row) => {
                const key = `${row.poolID || 'none'}-${batteryKey(row)}`;
                return (
                  <tr key={key}>
                    <td>{row.poolName}</td>
                    <td>{row.ownerEmail}</td>
                    <td>{row.ownerOrganization}</td>
                    <td>{row.batteryName}</td>
                    <td data-testid={`consumption-customer-credits-${key}`}>{row.creditsUsed}</td>
                    <td data-testid={`consumption-customer-remaining-${key}`}>
                      {row.creditsRemaining}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Dashboard>
  );
};

export default CreditConsumptionPage;
